import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Define the state interface
export interface AppState {
  theme: string;
  bagAmount: number;
  isAddingToBag: boolean;
  isAddedToBag: boolean;
  menuIsOpen: boolean;
  menuIsForceClosed: boolean;
  mobileMenuIsOpen: boolean;
  mobileMenuIsClosing: string | false;
  activeNav: string | false;
  activeNavMobile: string | false;
  isInNav: boolean;
  isInHeader: boolean;
  searchIsOpen: boolean;
  searchTerm: string | false;
  pageTitle: string | false;
  modalCountryIsOpen: boolean;
  modalPromoBannerIsOpen: boolean;
}

// Define initial state
const initialState: AppState = {
  theme: 'dark',
  bagAmount: 0,
  isAddingToBag: false,
  isAddedToBag: false,
  menuIsOpen: false,
  menuIsForceClosed: false,
  mobileMenuIsOpen: false,
  mobileMenuIsClosing: false,
  activeNav: false,
  activeNavMobile: false,
  isInNav: false,
  isInHeader: false,
  searchIsOpen: false,
  searchTerm: false,
  pageTitle: false,
  modalCountryIsOpen: false,
  modalPromoBannerIsOpen: false
};

// Define the context interface including all functions
export interface AppContextType {
  state: AppState;
  addToBag: () => void;
  closeBag: () => void;
  toggleSearch: () => void;
  openSearch: () => void;
  closeSearch: () => void;
  setSearchTerm: (term: string) => void;
  addPageTitle: (title: string) => void;
  removePageTitle: () => void;
  openCountryModal: () => void;
  closeCountryModal: () => void;
  openPromoBannerModal: () => void;
  closePromoBannerModal: () => void;
  setActiveNav: (nav: string) => void;
  unsetActiveNav: () => void;
  forceUnsetActiveNav: () => void;
  setActiveNavInner: (nav: string) => void;
  unsetActiveNavInner: () => void;
  setActiveNavMobile: (nav: string) => void;
  unsetActiveNavMobile: () => void;
  openMenuMobile: () => void;
  closeMenuMobile: () => void;
}

// Create context with default values
const defaultContextValue: AppContextType = {
  state: initialState,
  addToBag: () => {},
  closeBag: () => {},
  toggleSearch: () => {},
  openSearch: () => {},
  closeSearch: () => {},
  setSearchTerm: () => {},
  addPageTitle: () => {},
  removePageTitle: () => {},
  openCountryModal: () => {},
  closeCountryModal: () => {},
  openPromoBannerModal: () => {},
  closePromoBannerModal: () => {},
  setActiveNav: () => {},
  unsetActiveNav: () => {},
  forceUnsetActiveNav: () => {},
  setActiveNavInner: () => {},
  unsetActiveNavInner: () => {},
  setActiveNavMobile: () => {},
  unsetActiveNavMobile: () => {},
  openMenuMobile: () => {},
  closeMenuMobile: () => {}
};

// Create context with default value
export const AppContext = createContext<AppContextType>(defaultContextValue);

// Provider props interface
interface AppProviderProps {
  children: ReactNode;
}

// Provider component
export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, setState] = useState<AppState>(initialState);

  // Handle window resize for dynamic variables
  useEffect(() => {
    const handleResize = () => {
      const windowHeight = window.innerHeight;
      document.documentElement.style.setProperty('--window-height', `${windowHeight}px`);
      
      const promoBanner = document.getElementsByClassName('overlay-promobanner')[0];
      const promoBannerHeight = promoBanner ? promoBanner.clientHeight : 0;
      document.documentElement.style.setProperty('--promobanner-height', `${promoBannerHeight}px`);
    };
    
    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Bag functions
  const addToBag = () => {
    setState(prev => ({ ...prev, isAddingToBag: true }));
    
    setTimeout(() => {
      setActiveNav('bag');
      setActiveNavMobile('bag');
      setState(prev => ({ ...prev, isAddedToBag: true }));
      
      setState(prev => ({
        ...prev, 
        isAddingToBag: false,
        bagAmount: prev.bagAmount + 1
      }));
    }, 1500);
  };
  
  const closeBag = () => {
    forceUnsetActiveNav();
    unsetActiveNavMobile();
  };

  // Search functions
  const toggleSearch = () => {
    setState(prev => ({ ...prev, searchIsOpen: !prev.searchIsOpen }));
    document.body.classList.toggle('is-menu-open');
  };
  
  const openSearch = () => {
    setState(prev => ({ ...prev, searchIsOpen: true }));
    document.body.classList.add('is-menu-open');
  };
  
  const closeSearch = () => {
    setState(prev => ({ ...prev, searchIsOpen: false }));
    document.body.classList.remove('is-menu-open');
  };
  
  const setSearchTerm = (searchTerm: string) => {
    setState(prev => ({ ...prev, searchTerm }));
  };

  // Page title functions
  const addPageTitle = (pageTitle: string) => {
    setState(prev => ({ ...prev, pageTitle }));
  };
  
  const removePageTitle = () => {
    setState(prev => ({ ...prev, pageTitle: false }));
  };

  // Modal functions
  const openCountryModal = () => {
    setState(prev => ({ ...prev, modalCountryIsOpen: true }));
  };
  
  const closeCountryModal = () => {
    setState(prev => ({ ...prev, modalCountryIsOpen: false }));
  };
  
  const openPromoBannerModal = () => {
    setState(prev => ({ ...prev, modalPromoBannerIsOpen: true }));
  };
  
  const closePromoBannerModal = () => {
    setState(prev => ({ ...prev, modalPromoBannerIsOpen: false }));
  };

  // Navigation functions
  const setActiveNav = (nav: string) => {
    setState(prev => ({ ...prev, activeNav: nav, isInNav: false, isInHeader: true }));
    
    setTimeout(() => {
      setState(prev => ({ ...prev, isInHeader: false }));
    }, 20);
  };
  
  const unsetActiveNav = () => {
    setTimeout(() => {
      setState(prev => {
        if (!prev.isInNav && !prev.isInHeader) {
          return { ...prev, activeNav: false };
        }
        return prev;
      });
    }, 10);
    
    setTimeout(() => {
      setState(prev => ({ ...prev, isAddedToBag: false }));
    }, 1500);
  };
  
  const forceUnsetActiveNav = () => {
    setState(prev => ({ ...prev, activeNav: false, isInNav: false }));
    closeMenuMobile();
    
    setTimeout(() => {
      setState(prev => ({ ...prev, isAddedToBag: false }));
    }, 1500);
  };
  
  const setActiveNavInner = (nav: string) => {
    setState(prev => ({ ...prev, activeNav: nav, isInNav: true }));
  };
  
  const unsetActiveNavInner = () => {
    setState(prev => ({ ...prev, isInNav: false }));
  };
  
  const setActiveNavMobile = (nav: string) => {
    setState(prev => ({ ...prev, activeNavMobile: nav }));
    closeSearch();
    document.body.classList.add('is-menu-open');
  };
  
  const unsetActiveNavMobile = () => {
    setState(prev => ({ 
      ...prev, 
      mobileMenuIsClosing: prev.activeNavMobile as string, 
      activeNavMobile: false 
    }));
    
    setTimeout(() => {
      setState(prev => ({ ...prev, mobileMenuIsClosing: false }));
    }, 800);
    
    setTimeout(() => {
      setState(prev => ({ ...prev, isAddedToBag: false }));
    }, 1500);
  };
  
  const openMenuMobile = () => {
    document.getElementsByTagName('html')[0].classList.add('is-disabled');
    closeSearch();
    document.body.classList.add('is-menu-open');
    setState(prev => ({ ...prev, mobileMenuIsOpen: true }));
  };
  
  const closeMenuMobile = () => {
    document.getElementsByTagName('html')[0].classList.remove('is-disabled');
    
    setState(prev => ({ ...prev, mobileMenuIsOpen: false, activeNavMobile: false }));
    closeSearch();
    document.body.classList.remove('is-menu-open');
  };

  const contextValue: AppContextType = {
    state,
    addToBag,
    closeBag,
    toggleSearch,
    openSearch,
    closeSearch,
    setSearchTerm,
    addPageTitle,
    removePageTitle,
    openCountryModal,
    closeCountryModal,
    openPromoBannerModal,
    closePromoBannerModal,
    setActiveNav,
    unsetActiveNav,
    forceUnsetActiveNav,
    setActiveNavInner,
    unsetActiveNavInner,
    setActiveNavMobile,
    unsetActiveNavMobile,
    openMenuMobile,
    closeMenuMobile
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook for consuming the context
export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  return context;
};

// Consumer component for legacy compatibility
export const AppContextConsumer = AppContext.Consumer;
